/**
 * DUEL-V3-P1.5: the dive strike (ท่าโฉบ) made readable. While a fighter dives (rules: duelAirOf(w).dive) a shadow marks the
 * spot it will land on, filling in stepped rings as the descent runs (the tell to step out from under or guard); on landing
 * a small dust ring lasts a quarter second. FX pixel rules: whole pixels, stepped, a 1-px ink outline, a dithered fill, no
 * gradient or glow. Picture only.
 */
import { duelAirOf } from '@nine/shared';
import { registerRenderPass } from './registry';

const INK = '#2a1a0a';
const RED = '#c0392b';
const DUST = '#e8d9b0';
/** Where each diver last was going, to leave dust when the dive ends. */
const lastSpot = new Map<number, { x: number; y: number; at: number }>();
const DUST_LIFE = 0.25;

function ellipse(g: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, col: string): void {
  g.fillStyle = col;
  const n = Math.max(12, Math.round(rx * 2));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    g.fillRect(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry), 1, 1);
  }
}

registerRenderPass({
  id: 'duelAirTell',
  layer: 'ground',
  order: 202,
  draw({ g, world: w, iso, ox, oy }) {
    const sys = duelAirOf(w);
    const b = w.pvpBout;
    if (!sys || !b) {
      lastSpot.clear();
      return;
    }
    g.save();
    g.imageSmoothingEnabled = false;
    for (const id of [b.a, b.b]) {
      const d = sys.dive(id);
      if (d) {
        const [x, y] = iso.proj(d.x, d.y);
        const cx = Math.round(x + ox);
        const cy = Math.round(y + oy);
        lastSpot.set(id, { x: cx, y: cy, at: -1 });
        // Whole steps: a dark disc (dithered by a checker), a red ring closing in on it, a bright core near the end.
        const step = Math.min(3, Math.floor(d.k * 4));
        const rx = 16 - step * 2;
        const ry = Math.round(rx / 2);
        g.fillStyle = 'rgba(42,26,10,.5)';
        for (let yy = -ry; yy <= ry; yy++)
          for (let xx = -rx; xx <= rx; xx++)
            if ((xx * xx) / (rx * rx) + (yy * yy) / (ry * ry) <= 1 && ((xx + yy) & 1) === 0) g.fillRect(cx + xx, cy + yy, 1, 1);
        ellipse(g, cx, cy, rx + 1, ry + 1, INK);
        ellipse(g, cx, cy, rx, ry, step >= 2 ? '#f4d08c' : RED);
        continue;
      }
      const ls = lastSpot.get(id);
      if (ls) {
        if (ls.at < 0) ls.at = w.time;
        const k = (w.time - ls.at) / DUST_LIFE;
        if (k >= 1) lastSpot.delete(id);
        else {
          const f = Math.floor(k * 3);
          const r = 6 + f * 4;
          g.fillStyle = DUST;
          for (let i = 0; i < 8; i++) {
            const a = (i / 8) * Math.PI * 2 + f * 0.4;
            const px = Math.round(ls.x + Math.cos(a) * r);
            const py = Math.round(ls.y + Math.sin(a) * r * 0.5 - f);
            g.fillRect(px, py, f === 2 ? 1 : 2, f === 2 ? 1 : 2);
          }
        }
      }
    }
    g.restore();
  },
});
